"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { useEditorBundle, useBundleRoot } from "@/hooks/use-editor-bundle";
import { useAuth } from "@/lib/auth";
import { useHeaderAction } from "@/lib/header-action-context";
import { useCapabilityActions } from "@/hooks/use-capability-actions";
import { useCapabilityUpdate } from "@/hooks/use-entity-mutations";
import { CapabilityDetailLayout, WORKING_COPY_ID } from "@/components/capability-detail/CapabilityDetailLayout";
import type { DeferredOverviewDirtyPayload } from "@/components/capability-detail/CapabilityOverviewTab";
import { CapabilityHeaderActions } from "@/components/capability-detail/CapabilityHeaderActions";
import { CapabilityPickerDrawer, type CapabilityPickerItem } from "@/components/agent-config/CapabilityPickerDrawer";
import { MemoryRecordsBrowser } from "@/components/memory/MemoryRecordsBrowser";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Brain, Loader2, Pencil, X } from "lucide-react";
import { capabilityColors } from "@/lib/capability-colors";
import { api } from "@/lib/api-client";
import { toast } from "sonner";
import { logger } from "@/lib/telemetry";
import { cn } from "@/lib/utils";

const LOG_PREFIX = "memory-detail-page";
const HEADER_LAYER_ID = "memory-page";

function ModelCardContent({
   modelRef,
   appKey,
   models,
   bundle,
   onRemove,
   canRemove = true,
}: {
   modelRef: string;
   appKey: string;
   models: CapabilityPickerItem[];
   bundle: { localRefs?: Record<string, Record<string, unknown>> } | undefined;
   onRemove: () => void;
   canRemove?: boolean;
}) {
   const fromList = models.find(
      (x) =>
         x.name === modelRef ||
         (x.app_key && `${x.app_key}/${x.name}` === modelRef) ||
         x.fqcn === modelRef
   );
   const refId = modelRef.includes("/") ? modelRef : appKey ? `${appKey}/${modelRef}` : null;
   const fromBundle = refId && bundle?.localRefs?.[refId];
   const descFromBundle =
      fromBundle && typeof fromBundle === "object" && !Array.isArray(fromBundle)
         ? (fromBundle as Record<string, unknown>).description
         : undefined;
   const description =
      (fromList?.description?.trim() as string | undefined) ??
      (typeof descFromBundle === "string" ? descFromBundle : null);
   const displayName =
      fromList?.name ?? (modelRef.includes("/") ? modelRef.split("/").pop() : modelRef);

   return (
      <Card
         className={cn(
            "relative border-2 p-3 transition-all duration-200 min-h-[88px]",
            "border-l-4 border-slate-200 bg-white hover:shadow-md"
         )}
         style={{ borderLeftColor: capabilityColors.model.hex }}
      >
         <div className="flex flex-row items-start gap-3">
            <div
               className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
                  capabilityColors.model.bgLight,
                  capabilityColors.model.text
               )}
            >
               <Brain size={16} />
            </div>
            <div className="flex flex-col flex-1 min-w-0 gap-0.5">
               <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  embedding model
               </span>
               <span className="text-sm font-bold truncate text-slate-900">
                  {displayName}
               </span>
               {description && (
                  <span className="text-[10px] text-slate-500 line-clamp-2 leading-tight">
                     {description}
                  </span>
               )}
            </div>
            {canRemove && (
               <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 shrink-0 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50"
                  onClick={(e) => {
                     e.stopPropagation();
                     onRemove();
                  }}
                  aria-label="Remove model"
               >
                  <X size={10} />
               </Button>
            )}
         </div>
      </Card>
   );
}

interface MemoryCapabilityData {
   id?: string;
   name?: string;
   description?: string | null;
   tags?: string[];
   type?: string;
   config?: {
      embedding_model?: string;
   };
   working_revision?: {
      type_config?: {
         embedding_model?: string;
      };
   };
}

function extractMemoryState(data: MemoryCapabilityData | undefined) {
   if (!data) return { embeddingModel: "" };
   const config = data.config ?? {};
   const manifest = data.working_revision?.type_config ?? {};
   const embeddingModel =
      (config.embedding_model as string) ??
      (manifest.embedding_model as string) ??
      "";
   return { embeddingModel };
}

export default function MemoryDetailPage() {
   const params = useParams<{ appKey: string; name: string }>();
   const appKey = params.appKey;
   const name = params.name;
   const { identity, currentOrganization } = useAuth();
   const { data: bundle, isLoading, refetchWithoutCache } = useEditorBundle(appKey!, name!);
   const data = useBundleRoot(bundle);
   const updateMutation = useCapabilityUpdate(appKey!, name!);
   const { pushHeaderLayer, removeHeaderLayer } = useHeaderAction();
   const status = (data as { status?: string })?.status ?? "";
   const isArchived = status.toLowerCase() === "archived";

   const capabilityRef = appKey && name ? `${appKey}/${name}` : undefined;

   const [embeddingModel, setEmbeddingModel] = useState("");
   const [models, setModels] = useState<CapabilityPickerItem[]>([]);
   const [loadingModels, setLoadingModels] = useState(false);
   const [isModelDrawerOpen, setIsModelDrawerOpen] = useState(false);
   const [overviewPatch, setOverviewPatch] = useState<Record<string, unknown>>({});
   const [selectedRevisionId, setSelectedRevisionId] = useState(WORKING_COPY_ID);
   const isViewingPublished = selectedRevisionId !== WORKING_COPY_ID;

   const handleOverviewDeferredDirtyChange = useCallback((payload: DeferredOverviewDirtyPayload) => {
      setOverviewPatch((prev) => {
         const next = payload.patch;
         if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
         return next;
      });
   }, []);

   const formReadOnly = isArchived || !(data as Record<string, unknown> | undefined)?.working_revision || isViewingPublished;

   const memoryData = data as MemoryCapabilityData | undefined;

   const immutableRevisions = useMemo(
      () => ((data as Record<string, unknown>)?.immutable_revisions ?? []) as Array<{
         id: string; type_config?: Record<string, unknown>;
      }>,
      [data],
   );

   const selectedImmutableRevision = useMemo(
      () => immutableRevisions.find((r) => r.id === selectedRevisionId),
      [immutableRevisions, selectedRevisionId],
   );

   useEffect(() => {
      setLoadingModels(true);
      api.get("/capabilities", { params: { type: "model", include_entitled: "true" } })
         .then((res) => {
            const payload = res.data;
            const items = (
               Array.isArray(payload)
                  ? payload
                  : Array.isArray(payload?.data)
                     ? payload.data
                     : []
            ) as CapabilityPickerItem[];
            setModels(items);
         })
         .catch(() => setModels([]))
         .finally(() => setLoadingModels(false));
   }, []);

   useEffect(() => {
      if (isViewingPublished && selectedImmutableRevision) {
         const tc = selectedImmutableRevision.type_config ?? {};
         setEmbeddingModel((tc.embedding_model as string) ?? "");
         return;
      }
      const extracted = extractMemoryState(memoryData);
      setEmbeddingModel(extracted.embeddingModel);
   }, [memoryData?.config, memoryData?.working_revision?.type_config, isViewingPublished, selectedImmutableRevision]);

   const extractedSource = useMemo(
      () => extractMemoryState(memoryData),
      [memoryData?.config, memoryData?.working_revision?.type_config]
   );
   const isDirty = embeddingModel !== extractedSource.embeddingModel;
   const overviewDirty = Object.keys(overviewPatch).length > 0;
   const anyDirty = isDirty || overviewDirty;

   const handleSave = useCallback(() => {
      if (!appKey || !name) return;
      if (!isDirty && !overviewDirty) return;
      const payload: Record<string, unknown> = {};
      if (overviewDirty) {
         Object.assign(payload, overviewPatch);
      }
      if (isDirty) {
         payload.type_config = {
            embedding_model: embeddingModel || null,
         };
      }
      updateMutation.mutate(payload, {
         onSuccess: () => {
            setOverviewPatch({});
            toast.success("Memory configuration updated");
            logger.info(`${LOG_PREFIX} handleSave: memory updated`, {
               embeddingModel,
            });
         },
         onError: (err) => {
            logger.error(`${LOG_PREFIX} handleSave: update failed`, {
               error: err,
            });
            toast.error("Failed to update memory configuration");
         },
      });
   }, [
      appKey,
      name,
      isDirty,
      overviewDirty,
      overviewPatch,
      embeddingModel,
      updateMutation,
   ]);

   const actions = useCapabilityActions({
      appKey: appKey!,
      name: name!,
      typeLabel: "Memory",
      data: data as Record<string, unknown> | null,
      refetch: refetchWithoutCache,
      customSave: handleSave,
      customIsDirty: anyDirty,
      customIsSaving: updateMutation.isPending,
   });

   const saveRef = useRef(actions.save);
   saveRef.current = actions.save;
   const publishRef = useRef(actions.publish);
   publishRef.current = actions.publish;
   const editRef = useRef(actions.edit);
   editRef.current = actions.edit;

   useEffect(() => {
      if (!data || isArchived) {
         removeHeaderLayer(HEADER_LAYER_ID);
         return;
      }
      pushHeaderLayer(HEADER_LAYER_ID, {
         action: (
            <CapabilityHeaderActions
               typeLabel="Memory"
               hasWorkingRevision={actions.hasWorkingRevision}
               isViewingPublished={actions.isViewingPublished}
               isArchived={isArchived}
               selectedPublishedLabel={actions.selectedPublishedLabel}
               isDirty={actions.isDirty}
               isSaving={actions.isSaving}
               isPublishing={actions.isPublishing}
               onSave={() => void saveRef.current()}
               onPublish={() => void publishRef.current()}
               onEdit={() => editRef.current()}
               onArchive={() => actions.openArchiveDialog()}
               onSettings={() => actions.openSettings()}
            />
         ),
      });
      return () => removeHeaderLayer(HEADER_LAYER_ID);
   }, [
      data,
      isArchived,
      actions.hasWorkingRevision,
      actions.isViewingPublished,
      actions.selectedPublishedLabel,
      actions.isDirty,
      actions.isSaving,
      actions.isPublishing,
      pushHeaderLayer,
      removeHeaderLayer,
   ]);

   const modelSlot = (
      <div
         id="memory-embedding-model"
         role="button"
         tabIndex={formReadOnly ? -1 : 0}
         onClick={() => {
            if (!formReadOnly) setIsModelDrawerOpen(true);
         }}
         onKeyDown={(e) => {
            if (formReadOnly) return;
            if (e.key === "Enter" || e.key === " ") {
               e.preventDefault();
               setIsModelDrawerOpen(true);
            }
         }}
         className={cn(!formReadOnly && "cursor-pointer")}
      >
         {embeddingModel ? (
            <ModelCardContent
               modelRef={embeddingModel}
               appKey={appKey ?? ""}
               models={models}
               bundle={bundle}
               canRemove={!formReadOnly}
               onRemove={() => setEmbeddingModel("")}
            />
         ) : (
            <Card
               className={cn(
                  "border-2 border-dashed border-slate-200 bg-slate-50/50 p-3",
                  formReadOnly && "pointer-events-none opacity-60"
               )}
            >
               <div className="flex flex-col items-center justify-center py-2">
                  <span className="mb-1 text-amber-400">
                     <Brain size={24} />
                  </span>
                  <p className="text-xs font-medium text-amber-600">
                     {formReadOnly ? "No embedding model" : "Click to select embedding model"}
                  </p>
                  {loadingModels && !formReadOnly && (
                     <p className="text-xs text-slate-400 mt-1">Loading models...</p>
                  )}
                  <p className="text-xs text-slate-500 mt-2 italic">
                     A model capability configured for embeddings (e.g. text-embedding-3-small).
                     When set, memory records are embedded for vector similarity search.
                  </p>
               </div>
            </Card>
         )}
      </div>
   );

   const configSection = (
      <div className="flex flex-col gap-6">
         <div className="flex flex-col gap-6 rounded-xl border border-slate-200 bg-card p-6 text-card-foreground shadow-sm dark:border-slate-700">
            <h2 className="text-sm font-semibold text-slate-900">Memory Configuration</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
               <div className="space-y-2">
                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                     Embedding Model
                  </label>
                  {modelSlot}
               </div>
            </div>
         </div>

         <div className="flex flex-col gap-6 rounded-xl border border-slate-200 bg-card p-6 text-card-foreground shadow-sm dark:border-slate-700">
            <h2 className="text-sm font-semibold text-slate-900">Memory Records</h2>
            <MemoryRecordsBrowser capabilityRef={capabilityRef} />
         </div>
      </div>
   );

   if (isLoading) {
      return (
         <div className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
         </div>
      );
   }

   if (!data) {
      return (
         <div className="p-6">
            <p className="text-slate-600">Memory not found.</p>
         </div>
      );
   }

   return (
      <>
         <CapabilityDetailLayout
            typeLabel="Memory"
            appKey={appKey}
            capabilityName={name}
            selectedRevisionId={selectedRevisionId}
            onRevisionChange={setSelectedRevisionId}
            onOverviewDeferredDirtyChange={handleOverviewDeferredDirtyChange}
            pendingOverviewPatch={overviewPatch}
            onSaveDeferred={() => void handleSave()}
            isOverviewDirty={anyDirty}
            isSavingOverview={updateMutation.isPending}
            overviewFormDisabled={updateMutation.isPending || !actions.hasWorkingRevision || isViewingPublished}
         >
            {configSection}
         </CapabilityDetailLayout>
         <AlertDialog open={actions.replaceDialogOpen} onOpenChange={actions.setReplaceDialogOpen}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                     <Pencil size={18} className="text-amber-600" />
                     Replace working copy?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                     A working copy already exists. Replacing it will discard any unsaved changes
                     and create a new working copy based on the selected published version.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={actions.handleConfirmReplace}>
                     Replace
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>
         <CapabilityPickerDrawer
            isOpen={isModelDrawerOpen && !formReadOnly}
            onClose={() => setIsModelDrawerOpen(false)}
            slotType="model"
            items={models}
            selectedRefs={embeddingModel ? [embeddingModel] : []}
            onSelect={(modelName) => setEmbeddingModel(modelName)}
            appKey={appKey}
            currentUserId={identity?.sub}
            currentOrgId={currentOrganization?.id}
         />
      </>
   );
}
